// TextField — canonical `text` field primitive (comp-ε GAP-3b-i, CHG-2433).
// Single-line free text. Storage: string. Real implementation (not a
// CHG-1167 stub).
//
// Contract: specs/_product/field-primitive-registry.md §1 ('text') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): label associated to the input via FieldShell <label htmlFor=id>;
// validation error in a role="alert" live region; aria-invalid + aria-required
// on the input; aria-describedby links the input to its error + helper text
// nodes; native <input> focus order (no custom focus management needed).

import type { FieldPrimitiveProps } from "./types";
import { FieldShell, fieldControlStyle, useFieldIds, describedBy } from "./FieldShell";

export interface TextFieldProps extends FieldPrimitiveProps<string> {
  /** Optional input placeholder. */
  placeholder?: string;
  /** Optional max length forwarded to the input. */
  maxLength?: number;
}

export function TextField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
  placeholder,
  maxLength,
}: TextFieldProps) {
  const ids = useFieldIds();
  return (
    <FieldShell
      htmlFor={ids.inputId}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      errorId={ids.errorId}
      helperId={ids.helperId}
    >
      <input
        id={ids.inputId}
        name={name}
        type="text"
        value={value ?? ""}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(ids, error, helperText)}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        style={fieldControlStyle(Boolean(error))}
      />
    </FieldShell>
  );
}
